<template lang="pug">
	.product-data.flex-col(v-if="items && items.length")
		.flex-row.justify.wrap
			.widget.impressions.flex-col.anim1
				span.name {{$tCaption('Impressions')}}
				span.sum {{sum('impressions')}}
			.widget.clicks.flex-row.justify.anim1
				.flex-col
					span.name {{$tCaption('Clicks')}}
					span.sum {{sum('clicks')}}
				span.percent {{percent('clicks', 'impressions')}}
			.widget.engagements.flex-row.justify.anim1
				.flex-col
					span.name {{$tCaption('Engagements')}}
					span.sum {{sum('engagements')}}
				span.percent {{percent('engagements', 'impressions')}}
			.widget.visits.flex-row.justify.anim1
				.flex-col
					span.name {{$tCaption('Visits')}}
					span.sum {{sum('visits')}}
				span.percent {{percent('visits', 'impressions')}}
		.flex-row.justify(v-if="product.id === 'radio'")
			RadioTab(:trafficCampaignId="campaign.TrafficOrder")
		template(v-else)
			.flex-row.justify
				.widget.flex-col(v-if="product && product.id !== 'radio'")
					span.name.black-on-white {{$tCaption('Daily stats chart')}}
					DxChart(:data-source="groupedByDateData", :size="{ height: 280, width: 'auto' }", :palette="palette")
						DxSeries(value-field="visits", :name="$tCaption('Visits')", axis="clicks")
						DxSeries(value-field="clicks", :name="$tCaption('Clicks')", axis="clicks")
						DxSeries(value-field="engagements", :name="$tCaption('Engagements')", axis="clicks")
						DxSeries(value-field="displayRemarketingImpressions", type="stackedArea", :name="$tCaption('Remarketing impressions')", axis="impressions")
						DxSeries(value-field="impressions", type="stackedArea", :name="$tCaption('Impressions')", axis="impressions")
						DxLegend(:visible="true")
						DxCommonSeriesSettings(type="stackedBar", argument-field="name", :ignoreEmptyPoints="true")
						DxTooltip(:enabled="true", :zIndex="100000", format="fixedPoint", :customizeTooltip="customizeTooltip")
						DxValueAxis(:visible="true", :title="{ text: $tCaption('Impressions') }", name="impressions", position="left", color="#dedede", :grid="{ color: '#efefef' }", :tick="{ color: '#00000000' }", :label="{ font: { color: '#333' }}")
						DxValueAxis(:visible="true", :title="{ text: $tCaption('Clicks/Visits') }", name="clicks", position="right", color="#dedede", :grid="{ color: '#efefef' }", :tick="{ color: '#00000000' }", :label="{ font: { color: '#333' }}")
						DxArgumentAxis(:visible="true", type="discrete", argumentType="datetime", color="#efefef", :grid="{ color: '#efefef' }", :tick="{ color: '#00000000' }",
							:label="{ textOverflow: 'Ellipsis', overlappingBehavior: 'rotate', rotationAngle: 30, format: 'MM/dd/yyyy', font: { color: '#333' }}")
			.flex-row.justify
				.widget.flex-col
					span.name.black-on-white {{$tCaption('Top cities')}}
					DxDataGrid.compact(width="100%", :data-source="top10CitiesData", :show-borders="true")
						DxColumn(data-field="name", :caption="$tCaption('City')")
						DxColumn(data-field="impressions", :caption="$tCaption('Impressions')", format="fixedPoint")
						DxColumn(data-field="displayRemarketingImpressions", :caption="$tCaption('RTG Impressions')", format="fixedPoint")
						DxColumn(data-field="clicks", :caption="$tCaption('Clicks')", format="fixedPoint", sort-order="desc")
						DxColumn(data-field="displayRemarketingClicks", :caption="$tCaption('RTG Clicks')", format="fixedPoint")
						DxColumn(data-field="ctr", :caption="$tCaption('Click rate')", :format="{ type: 'percent', precision: 2 }")
						DxColumn(data-field="rtgCtr", :caption="$tCaption('RTG Click rate')", :format="{ type: 'percent', precision: 2 }")
						DxColumn(data-field="viewThroughs", :caption="$tCaption('View throughs')", format="fixedPoint")
						DxColumn(data-field="viewThroughRate", :caption="$tCaption('View through rate')", :format="{ type: 'percent', precision: 2 }")
			.flex-row.justify
				.widget.flex-col.space
					span.name.black-on-white {{$tCaption('Devices')}}
					DxPieChart(:data-source="groupedByDevicesData",
						resolve-label-overlapping="shift",
						type="doughnut",
						:size="{ height: 280 }", :rotated="true",
						:series="[pieChartSerieConfig()]",
						:legend="{ visible: true }",
						:inner-radius="0.65",
						palette="Soft Pastel",
						center-template="centerTemplate",
						@point-click="(e) => onPointClick(e, 'gender')"
					)
						DxTooltip(:enabled="true", :zIndex="100000", format="fixedPoint", :customizeTooltip="(e) => ({ text: `${e.valueText}\n${e.argument}` })")
						template(v-slot:centerTemplate="center")
							svg
								text(text-anchor="middle"
									x="100"
									y="120"
								)
									tspan(:style="{fontSize: 50, fontName: 'Roboto', fontWeight: 100, fill: '#333', letterSpacing: '-4px' }", x="100") {{max(groupedByDevicesData, 'impressions').percent}}%
									tspan(:style="{fontSize: 20, fontName: 'Roboto', fontWeight: 100, fill: '#333' }", x="100", dy="20px") {{max(groupedByDevicesData, 'impressions').name}}
					DxDataGrid.compact(width="100%", :data-source="groupedByDevicesData", :show-borders="true")
						DxColumn(data-field="name", :caption="$tCaption('Device')")
						DxColumn(data-field="impressions", :caption="$tCaption('Impressions')", format="fixedPoint", sort-order="desc")
						DxColumn(data-field="clicks", :caption="$tCaption('Clicks')", format="fixedPoint")
						DxColumn(data-field="ctr", :caption="$tCaption('Click rate')", :format="{ type: 'percent', precision: 2 }")
				.widget.flex-col
					span.name.black-on-white {{$tCaption('Placements')}}
					DxDataGrid.compact(width="100%", :data-source="top10PlacementData", :show-borders="true")
						DxColumn(data-field="name", :caption="$tCaption('Domain')")
						DxColumn(data-field="impressions", :caption="$tCaption('Impressions')", format="fixedPoint")
						DxColumn(data-field="clicks", :caption="$tCaption('Clicks')", format="fixedPoint", sort-order="desc")
						DxColumn(data-field="ctr", :caption="$tCaption('Click rate')", :format="{ type: 'percent', precision: 2 }")
</template>

<script>
	import _ from 'lodash';
	import VueTypes from 'vue-types';
	import Data from 'app-core/Data';
	import moment from 'moment';
	import {DxChart, DxSeries, DxLegend, DxSize, DxCommonSeriesSettings, DxTooltip, DxValueAxis, DxArgumentAxis, DxGrid, DxLabel, DxVisualRange, DxWholeRange, DxSeriesTemplate, DxFormat} from 'devextreme-vue/chart';
	import {DxDataGrid, DxColumn, DxSelection, DxSummary, DxTotalItem} from 'devextreme-vue/data-grid';
	import DxPieChart from 'devextreme-vue/pie-chart';
	import RadioTab from './RadioTab.vue';

	export default {
		translationCategory: 'dashboard.ProductData',
		name: 'product-data',
		components: {
			DxChart, DxSeries, DxLegend, DxSize, DxCommonSeriesSettings, DxTooltip, DxValueAxis, DxArgumentAxis, DxGrid, DxLabel, DxVisualRange, DxWholeRange, DxSeriesTemplate, DxFormat,
			DxDataGrid, DxColumn, DxSelection, DxSummary, DxTotalItem,
			DxPieChart,
			RadioTab
		},
		props: {
			items: VueTypes.array.def([]),
			product: VueTypes.object.def({}),
			campaign: VueTypes.object.def(null)
		},
		data() {
			return {
				palette: ['#2e91af', '#4aa97d', '#af5a95', '#bebebe', '#dedede']
			};
		},
		computed: {
			groupedByDateData() {
				return this.groupAndCalcTotals('date');
			},
			top10CitiesData() {
				return this.groupAndCalcTotals('city').sort((a,b) => b.clicks - a.clicks).slice(0,10);
			},
			groupedByDevicesData() {
				return this.groupAndCalcTotals('device');
			},
			top10PlacementData() {
				return this.groupAndCalcTotals('domain').sort((a,b) => b.clicks - a.clicks).slice(0,10);
			}
		},
		mounted() {

		},
		destroyed() {

		},
		methods: {
			groupAndCalcTotals(prop) {
				var groups = _.groupBy(this.items, v => JSON.stringify({ name: v[prop] }));
				var result = [];
				for(var groupString in groups) {
					var group = JSON.parse(groupString);
					var data = groups[groupString];
					group.impressions = _.sumBy(data, f => f.impressions || 0);
					group.clicks = _.sumBy(data, f => f.clicks || 0);
					group.engagements = _.sumBy(data, f => f.engagements || 0);
					group.visits = _.sumBy(data, f => f.visits || 0);
					group.displayRemarketingImpressions = _.sumBy(data, f => f.displayRemarketingImpressions || 0);
					group.displayRemarketingClicks = _.sumBy(data, f => f.displayRemarketingClicks || 0);
					group.viewThroughs = _.sumBy(data, f => f.viewThroughs || 0);
					group.ctr = group.clicks / group.impressions;
					group.viewThroughRate = group.viewThroughs / group.impressions;
					group.rtgCtr = group.displayRemarketingClicks / group.impressions;

					if (group.impressions)
						result.push(group);
				}

				if (__DEBUG__)
					console.info(`${prop} data grouped data set calculated`, { result });

				return result;
			},
			max(source, prop) {
				var item = _.maxBy(source, prop) || {};
				var total = _.sumBy(source, prop) || 0;
				if (!total) return 0;
				return {...item, percent: Math.floor(parseFloat(item[prop] * 100) / total) || 0};
			},
			pieChartSerieConfig() {
				return {
					argumentField: 'name',
					valueField: 'impressions',
					label: {
						visible: false,
						backgroundColor: 'none',
						format: 'fixedPoint',
						connector: {
							visible: true
						},
						customizeText: ({ argumentText }) => `${argumentText}`
					}
				};
			},
			sum(prop, format) {
				return Data.formatNumber(_.sumBy(this.items, v => v[prop]) || 0, format || 'fixedPoint');
			},
			percent(prop1, prop2, format) {
				return Data.formatNumber(_.sumBy(this.items, v => v[prop1]) / _.sumBy(this.items, v => v[prop2]) || 0, format || '#0.#%');
			},
			customizeTooltip(e) {
				return ({ text: `${e.valueText} ${e.seriesName}\n${moment(e.originalArgument).format('MM/DD/YYYY')}` });
			}
		}
	};
</script>
<style lang='scss'>
	.product-data {
		.widget {
			background: rgb(251, 251, 251);
			padding: 10px;
			flex: 1;
			-webkit-box-shadow: 3px 3px 3px -2px rgba(27, 9, 9, 0.2);
			-moz-box-shadow: 3px 3px 3px -2px rgba(27, 9, 9, 0.2);
			box-shadow: 3px 3px 3px -2px rgba(27, 9, 9, 0.2);
			border: 4px solid rgba(128, 128, 128, 0.1);
			border-radius: 4px;
			color: #ffffffa3;

			.name {
				color: rgba(255, 255, 255, 0.45);
				text-transform: uppercase;
				font-size: 18px;
				font-weight: 100;
			}

			.black-on-white {
				color: rgba(0,0,0,0.45);
			}

			.sum {
				font-size: 26px;
				font-weight: 100;
			}

			.percent {
				font-size: 20px;
				font-weight: 100;
			}

			&.impressions {
				background: #837bc1;
			}

			&.clicks {
				background: #4aa97d;
			}

			&.engagements {
				background: #af5a95;
			}

			&.visits {
				background: #2e91af;
			}
		}

		/* ANIMATIONS */
		.anim1 {
			-webkit-animation: flip-in-hor-bottom 0.5s cubic-bezier(0.250, 0.460, 0.450, 0.940) both;
					animation: flip-in-hor-bottom 0.5s cubic-bezier(0.250, 0.460, 0.450, 0.940) both;
		}

		@-webkit-keyframes flip-in-hor-bottom {
			0% {
				-webkit-transform: rotateX(80deg);
						transform: rotateX(80deg);
				opacity: 0;
			}
			100% {
				-webkit-transform: rotateX(0);
						transform: rotateX(0);
				opacity: 1;
			}
		}
		@keyframes flip-in-hor-bottom {
			0% {
				-webkit-transform: rotateX(80deg);
						transform: rotateX(80deg);
				opacity: 0;
			}
			100% {
				-webkit-transform: rotateX(0);
						transform: rotateX(0);
				opacity: 1;
			}
		}
	}
</style>
